// ✅ IMPROVED GUARD SCANNER UI
// - Same orange/blue theme
// - Better guard-facing layout
// - Status chips, emojis, guardian cards, accompanying photo card
// - Mark IN disabled until Mark OUT is completed
// - Scanner restart fixed with scannerKey

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import { Html5Qrcode } from "html5-qrcode";
import { API_URL, API_UPLOAD_URL } from "./config";
import { formatDateTimeIST } from "./commonfunctions/dateUtils";
import { startAuthentication } from "@simplewebauthn/browser";

const GuardScanner = () => {
  const qrRef = useRef(null);
  const startedRef = useRef(false);
  const stoppingRef = useRef(false);
  const lastTextRef = useRef("");

  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [scannedToken, setScannedToken] = useState("");
  const [scannerKey, setScannerKey] = useState(0);

  const [accompanyingPhotoFile, setAccompanyingPhotoFile] = useState(null);
  const [accompanyingPhotoPreview, setAccompanyingPhotoPreview] = useState("");

  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";

  const isOutDone = !!result?.usage?.outUsed;
  const isInDone = !!result?.usage?.inUsed;

  const showToast = (msg, sev = "success") => setToast({ open: true, msg, sev });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const getUploadUrl = (value) => {
    if (!value) return "";
    if (value.startsWith("blob:") || value.startsWith("http")) return value;
    return `${API_UPLOAD_URL}/uploads/${value}`;
  };

  const handleAccompanyingPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (accompanyingPhotoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(accompanyingPhotoPreview);
    }

    const url = URL.createObjectURL(file);
    setAccompanyingPhotoFile(file);
    setAccompanyingPhotoPreview(url);
  };

  const pageWrapSx = {
    minHeight: "100vh",
    p: 2,
    backgroundColor: BG,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  };

  const rootPaperSx = {
    width: "100%",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const readerWrapSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: 8,
      pointerEvents: "none",
      border: "1px dashed rgba(25,118,210,0.25)",
    },
  };

  const statusChipSx = {
    borderRadius: 2,
    fontWeight: 900,
    px: 1,
  };

  const miniCardSx = {
    borderRadius: 2,
    border: `1px solid ${BORDER}`,
    backgroundColor: "rgba(255,255,255,0.95)",
    p: 1.5,
  };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (result) return;

      try {
        qrRef.current = new Html5Qrcode(`qr-reader-${scannerKey}`);
        const qr = qrRef.current;

        await qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            if (cancelled) return;
            if (busy || result) return;
            if (decodedText === lastTextRef.current) return;

            lastTextRef.current = decodedText;
            setBusy(true);
            setError("");

            try {
              const resp = await axios.post(`${API_URL}/api/guard/verify-qr`, {
                token: decodedText,
              });

              if (cancelled) return;

              setScannedToken(decodedText);
              setResult(resp.data);
            } catch (e) {
              console.error(e);
              if (!cancelled) setError("Verification failed");
              lastTextRef.current = "";
            } finally {
              if (!cancelled) setBusy(false);
            }
          },
          () => {}
        );

        if (!cancelled) startedRef.current = true;
      } catch (e) {
        console.error("Camera start failed:", e);
        if (!cancelled) setError("Camera permission / start failed");
      }
    };

    init();

    return () => {
      cancelled = true;
      const qr = qrRef.current;
      if (!qr) return;

      if (startedRef.current && !stoppingRef.current) {
        stoppingRef.current = true;

        const p = qr.stop();

        if (p && typeof p.finally === "function") {
          p.catch(() => {}).finally(() => {
            try {
              qr.clear();
            } catch {}
            startedRef.current = false;
            stoppingRef.current = false;
          });
        } else {
          try {
            qr.clear();
          } catch {}
          startedRef.current = false;
          stoppingRef.current = false;
        }
      } else {
        try {
          qr.clear();
        } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerKey]);

  useEffect(() => {
    const stopIfNeeded = async () => {
      const qr = qrRef.current;
      if (!qr) return;
      if (!result) return;
      if (!startedRef.current) return;
      if (stoppingRef.current) return;

      stoppingRef.current = true;

      try {
        await qr.stop();
      } catch {
        // ignore
      } finally {
        try {
          qr.clear();
        } catch {}
        startedRef.current = false;
        stoppingRef.current = false;
      }
    };

    stopIfNeeded();
  }, [result]);

  const scanNext = async () => {
    setResult(null);
    setError("");
    setBusy(false);
    setScannedToken("");
    setAccompanyingPhotoFile(null);

    if (accompanyingPhotoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(accompanyingPhotoPreview);
    }

    setAccompanyingPhotoPreview("");
    lastTextRef.current = "";

    const qr = qrRef.current;

    if (qr && startedRef.current && !stoppingRef.current) {
      stoppingRef.current = true;

      try {
        await qr.stop();
      } catch {}

      try {
        await qr.clear();
      } catch {}

      startedRef.current = false;
      stoppingRef.current = false;
    }

    qrRef.current = null;
    setScannerKey((k) => k + 1);
  };

  const handleMark = async (direction) => {
    if (!scannedToken) {
      showToast("Scan a QR first", "error");
      return;
    }

    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const campusCode =
        result?.campusCode ||
        result?.student?.campusCode ||
        result?.student?.CampusCode;

      if (!campusCode) {
        throw new Error("CampusCode not found in QR verification result. Please re-scan.");
      }

      let accompanyingPhoto = null;

      if (accompanyingPhotoFile) {
        const fd = new FormData();
        fd.append("CampusCode", campusCode);
        fd.append("TimeOffRequestID", result?.timeOffRequestID || result?.torId || "");
        fd.append("Direction", direction);
        fd.append("accompanyingPhoto", accompanyingPhotoFile);

        {/*
        const uploadResp = await axios.post(
          `${API_URL}/api/guard/upload-accompanying-photo`,
          fd,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        */}

        const uploadResp = await axios.post(
          `${API_URL}/api/guard/upload-accompanying-photo`,fd);

        accompanyingPhoto = uploadResp.data?.filename || null;
      }

      const optsResp = await axios.post(`${API_URL}/api/guard/passkey/options`, {
        campusCode,
      });

      const options = optsResp.data;

      if (!options?.challenge) {
        throw new Error("Passkey options missing challenge");
      }

      const assertion = await startAuthentication(options);

      const markResp = await axios.post(`${API_URL}/api/guard/mark`, {
        token: scannedToken,
        direction,
        assertion,
        campusCode,
        accompanyingPhoto,
      });

      showToast(markResp.data?.message || "Done", "success");

      const resp2 = await axios.post(`${API_URL}/api/guard/verify-qr`, {
        token: scannedToken,
      });

      setResult(resp2.data);
    } catch (e) {
      console.error(e);

      const msg = e?.response?.data?.message || e?.message || "Failed";

      setError(msg);
      showToast(msg, "error");
    } finally {
      setBusy(false);
    }
  };

  const studentPhotoSrc = result?.student?.photoUrl
    ? `${API_UPLOAD_URL}/uploads/${result.student.photoUrl}`
    : "";

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 900, lineHeight: 1.2 }}>
              🛡️ Guard Time-Off Scanner
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Scan QR, verify identity, capture accompanying person, and mark OUT/IN
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  📷 Scanner
                </Typography>

                <Chip
                  size="small"
                  label={result ? "QR Captured" : busy ? "Scanning..." : "Ready"}
                  sx={{
                    ...statusChipSx,
                    backgroundColor: result
                      ? "rgba(46,125,50,0.10)"
                      : "rgba(25,118,210,0.08)",
                    color: result ? "rgba(46,125,50,1)" : "rgba(25,118,210,0.95)",
                  }}
                />
              </Stack>

              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 1.5 }}>
                Point the camera at the student’s time-off QR code.
              </Typography>

              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box sx={readerWrapSx}>
                <Box
                  key={scannerKey}
                  id={`qr-reader-${scannerKey}`}
                  sx={{
                    width: "100%",
                    minHeight: 320,
                    "& video": {
                      width: "100% !important",
                      borderRadius: 8,
                    },
                    "& img": {
                      borderRadius: 8,
                    },
                  }}
                />
              </Box>

              {busy && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(25,118,210,0.18)",
                    backgroundColor: "rgba(25,118,210,0.04)",
                  }}
                >
                  🔍 Verifying...
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(244,67,54,0.25)",
                    backgroundColor: "rgba(244,67,54,0.04)",
                  }}
                >
                  {error}
                </Alert>
              )}
            </Box>
          </Paper>

          {result && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2, p: 2 }}>
              {result.ok ? (
                <>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: "rgba(46,125,50,0.08)",
                      border: "1px solid rgba(46,125,50,0.20)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: "1.25rem",
                        color: "rgba(46,125,50,1)",
                      }}
                    >
                      ✅ APPROVED
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                      Verify student, guardian, and accompanying person before marking.
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                    <Box sx={{ ...miniCardSx, flex: "0 0 175px", mx: { xs: "auto", sm: 0 } }}>
                      <Avatar
                        src={studentPhotoSrc}
                        variant="square"
                        sx={{
                          width: 145,
                          height: 185,
                          m: "auto",
                          borderRadius: 2,
                          border: "1px solid rgba(25,118,210,0.20)",
                          backgroundColor: "rgba(25,118,210,0.03)",
                        }}
                      />
                      <Typography sx={{ mt: 1, textAlign: "center", fontWeight: 900 }}>
                        👨‍🎓 Student
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 260 }}>
                      <Paper elevation={0} sx={miniCardSx}>
                        <Typography sx={{ fontWeight: 900, fontSize: "1.1rem" }}>
                          {result.student?.name}
                        </Typography>

                        <Typography sx={{ opacity: 0.8 }}>
                          🆔 ID: {result.student?.studentID}
                        </Typography>

                        <Divider sx={{ my: 1.5, borderColor: "rgba(25,118,210,0.12)" }} />

                        <Typography sx={{ opacity: 0.9, fontWeight: 700 }}>
                          🗓️ Leave Window
                        </Typography>

                        <Typography sx={{ opacity: 0.9 }}>
                          From: {formatDateTimeIST(result.leave.from)} <br />
                          To: {formatDateTimeIST(result.leave.to)}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                          <Chip
                            label={isOutDone ? "✅ OUT Done" : "⏳ OUT Pending"}
                            sx={{
                              ...statusChipSx,
                              backgroundColor: isOutDone
                                ? "rgba(46,125,50,0.10)"
                                : "rgba(240,120,40,0.12)",
                              color: isOutDone ? "rgba(46,125,50,1)" : THEME_ORANGE,
                            }}
                          />

                          <Chip
                            label={isInDone ? "✅ IN Done" : "⏳ IN Pending"}
                            sx={{
                              ...statusChipSx,
                              backgroundColor: isInDone
                                ? "rgba(46,125,50,0.10)"
                                : "rgba(25,118,210,0.08)",
                              color: isInDone
                                ? "rgba(46,125,50,1)"
                                : "rgba(25,118,210,0.95)",
                            }}
                          />
                        </Stack>
                      </Paper>

                      <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

                      <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                        🧑‍🤝‍🧑 Registered Guardian Photos
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        {(result.student?.guardians || []).map((g, idx) => {
                          const imgSrc = getUploadUrl(g.image);

                          return (
                            <Paper
                              key={idx}
                              elevation={0}
                              sx={{
                                ...miniCardSx,
                                width: 130,
                                textAlign: "center",
                              }}
                            >
                              <Avatar
                                src={imgSrc}
                                variant="square"
                                sx={{
                                  width: 110,
                                  height: 135,
                                  mx: "auto",
                                  borderRadius: 2,
                                  border: "1px solid rgba(25,118,210,0.20)",
                                  backgroundColor: "rgba(25,118,210,0.03)",
                                }}
                              />

                              <Typography sx={{ mt: 0.75, fontSize: "0.8rem", fontWeight: 900 }}>
                                {g.name || `Guardian ${idx + 1}`}
                              </Typography>
                            </Paper>
                          );
                        })}
                      </Box>

                      <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

                      <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                        📸 Accompanying Person Photo
                      </Typography>

                      <Paper elevation={0} sx={{ ...miniCardSx }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <Avatar
                            src={accompanyingPhotoPreview}
                            variant="square"
                            sx={{
                              width: 125,
                              height: 145,
                              borderRadius: 2,
                              border: "1px solid rgba(25,118,210,0.20)",
                              backgroundColor: "rgba(25,118,210,0.03)",
                            }}
                          />

                          <Box sx={{ flex: 1, minWidth: 220 }}>
                            <Typography sx={{ fontWeight: 800 }}>
                              Capture the person accompanying the student.
                            </Typography>

                            <Typography
                              sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 1 }}
                            >
                              This photo will be saved with the OUT/IN scan record.
                            </Typography>

                            <Button variant="outlined" component="label" sx={outlinedBtnSx}>
                              📷 Click Photo
                              <input
                                hidden
                                type="file"
                                accept="image/*"
                                capture="user"
                                onChange={handleAccompanyingPhotoSelect}
                              />
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2, display: "grid", gap: 1.2 }}>
                    <Button
                      fullWidth
                      sx={{
                        ...primaryBtnSx,
                        py: 1.2,
                        fontSize: "1rem",
                        opacity: result.usage?.outUsed || !accompanyingPhotoFile ? 0.65 : 1,
                      }}
                      variant="contained"
                      disabled={result.usage?.outUsed || busy || !accompanyingPhotoFile}
                      onClick={() => handleMark("OUT")}
                    >
                      🚪 Mark OUT
                    </Button>

                    {!result.usage?.outUsed && !accompanyingPhotoFile && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        📸 Please upload/click accompanying person photo before Mark OUT.
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      sx={{
                        ...outlinedBtnSx,
                        py: 1.2,
                        fontSize: "1rem",
                        opacity: !result.usage?.outUsed || result.usage?.inUsed ? 0.55 : 1,
                      }}
                      variant="outlined"
                      disabled={!result.usage?.outUsed || result.usage?.inUsed || busy || !accompanyingPhotoFile}
                      onClick={() => handleMark("IN")}
                    >
                      🏫 Mark IN
                    </Button>

                    {!result.usage?.outUsed && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        ⚠️ Mark IN is locked until OUT is completed.
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      sx={{
                        ...primaryBtnSx,
                        mt: 0.5,
                        backgroundColor: "rgba(25,118,210,0.95)",
                        "&:hover": { backgroundColor: "rgba(25,118,210,1)" },
                      }}
                      variant="contained"
                      onClick={scanNext}
                      disabled={busy}
                    >
                      🔄 Scan Next Student
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: 2,
                      border: "1px solid rgba(244,67,54,0.25)",
                      backgroundColor: "rgba(244,67,54,0.04)",
                    }}
                  >
                    ❌ {result.reason}
                  </Alert>

                  <Button
                    fullWidth
                    sx={{ ...primaryBtnSx, mt: 2 }}
                    variant="contained"
                    onClick={scanNext}
                    disabled={busy}
                  >
                    🔄 Scan Next Student
                  </Button>
                </>
              )}
            </Paper>
          )}
        </Box>
      </Paper>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={closeToast}>
        <Alert severity={toast.sev} onClose={closeToast} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GuardScanner;